import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Box, RadioGroup, Radio, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  CALLOUT_BACKGROUND,
  CALLOUT_FOREGROUND,
  OUTLINED_INPUT_FOCUS_BORDER,
} from 'lib/form-field-styles'
import { SEGMENTED_BUTTON_BORDER } from 'lib/segmented-button-styles'
import {
  composeOptionBorder,
  composeOptionInteractionStates,
  composeSelectedCalloutSurface,
  SELECTED_CHIP_FOREGROUND,
} from 'pages/pga-coach/book-student/coach-book-student-compose-styles'

// Mirrors the icon option tiles in the "Create student" modal
// (components/add-student-modal/tile-buttons.js) and the selected-card surface
// used by the coach fast-book compose modal.
const useStyles = makeStyles(theme => ({
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    width: '100%',
    paddingTop: theme.spacing(1),
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxSizing: 'border-box',
    width: '100%',
    margin: 0,
    padding: theme.spacing(2, 2.5),
    borderRadius: 12,
    border: composeOptionBorder,
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    ...composeOptionInteractionStates,
    '&:has(:focus-visible)': {
      outline: `2px solid ${OUTLINED_INPUT_FOCUS_BORDER}`,
      outlineOffset: 2,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.75, 2),
      gap: theme.spacing(1.5),
    },
  },
  cardSelected: {
    ...composeSelectedCalloutSurface,
    '& $icon': {
      backgroundColor: theme.palette.common.white,
      color: SELECTED_CHIP_FOREGROUND,
    },
    '& $title': {
      color: SELECTED_CHIP_FOREGROUND,
    },
  },
  cardDisabled: {
    cursor: 'default',
    '&, &:hover': {
      borderColor: SEGMENTED_BUTTON_BORDER,
      backgroundColor: theme.palette.common.white,
    },
    '&$cardSelected, &$cardSelected:hover': {
      borderColor: OUTLINED_INPUT_FOCUS_BORDER,
      backgroundColor: composeSelectedCalloutSurface.backgroundColor,
    },
    '&:not($cardSelected)': {
      opacity: 0.6,
    },
  },
  icon: {
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: '50%',
    backgroundColor: CALLOUT_BACKGROUND,
    color: CALLOUT_FOREGROUND,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiSvgIcon-root, & svg': {
      width: 16,
      height: 16,
      fontSize: 16,
    },
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: CALLOUT_FOREGROUND,
  },
  description: {
    margin: theme.spacing(0.5, 0, 0),
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  radio: {
    flexShrink: 0,
    padding: theme.spacing(0.5),
    color: '#40484C',
    '&.Mui-checked': {
      color: OUTLINED_INPUT_FOCUS_BORDER,
    },
  },
}))

const getOptionId = (idPrefix, value, part) => `${idPrefix}-${value}-${part}`

const OfferingTypeCardGroup = ({
  options,
  value,
  onChange,
  readOnly = false,
  labelledBy,
  describedBy,
  ariaLabel,
  idPrefix = 'offering-type',
}) => {
  const classes = useStyles()

  return (
    <RadioGroup
      className={classes.group}
      value={value}
      onChange={onChange}
      aria-label={ariaLabel}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
    >
      {options.map(({ value: optionValue, title, description, icon: Icon }) => {
        const selected = optionValue === value
        return (
          <Box
            key={optionValue}
            component="label"
            className={clsx(
              classes.card,
              selected && classes.cardSelected,
              readOnly && classes.cardDisabled,
            )}
          >
            <Box className={classes.icon} aria-hidden="true">
              <Icon />
            </Box>
            <Box className={classes.body}>
              <Typography
                id={getOptionId(idPrefix, optionValue, 'title')}
                className={classes.title}
              >
                {title}
              </Typography>
              <Typography
                id={getOptionId(idPrefix, optionValue, 'description')}
                className={classes.description}
              >
                {description}
              </Typography>
            </Box>
            <Radio
              value={optionValue}
              disabled={readOnly}
              color="default"
              className={classes.radio}
              data-cy={
                {
                  lessonPack: 'lessonPack',
                  virtualCoaching: 'virtualCoaching',
                }[optionValue] || 'single'
              }
              inputProps={{
                'aria-labelledby': getOptionId(idPrefix, optionValue, 'title'),
                'aria-describedby': getOptionId(
                  idPrefix,
                  optionValue,
                  'description',
                ),
              }}
            />
          </Box>
        )
      })}
    </RadioGroup>
  )
}

OfferingTypeCardGroup.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      icon: PropTypes.elementType.isRequired,
    }),
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  labelledBy: PropTypes.string,
  describedBy: PropTypes.string,
  ariaLabel: PropTypes.string,
  idPrefix: PropTypes.string,
}

export default OfferingTypeCardGroup
